(() => {
  const scrollToItem = (track, item) => {
    if (!track || !item) return;
    const trackBox = track.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    const style = getComputedStyle(track);
    const padding = parseFloat(style.paddingInlineStart) || 0;
    const distance = style.direction === 'rtl'
      ? itemBox.right - trackBox.right + padding
      : itemBox.left - trackBox.left - padding;
    track.scrollTo({ left: track.scrollLeft + distance, behavior: 'instant' });
  };

  const nearestItem = (track, items) => {
    const rect = track.getBoundingClientRect();
    const style = getComputedStyle(track);
    const rtl = style.direction === 'rtl';
    const edge = rtl ? rect.right - (parseFloat(style.paddingInlineStart) || 0) : rect.left + (parseFloat(style.paddingInlineStart) || 0);
    return items.reduce((best, item) => {
      const box = item.getBoundingClientRect();
      const distance = Math.abs((rtl ? box.right : box.left) - edge);
      return distance < best.distance ? { item, distance } : best;
    }, { item: items[0], distance: Infinity }).item;
  };

  if (!customElements.get('noor-product')) {
    customElements.define('noor-product', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const options = { signal: this.abort.signal };
        this.quantity = this.querySelector('[name="quantity"]');
        this.addButton = this.querySelector('[data-add-to-cart]');
        this.plan = this.querySelector('[data-selling-plan]');
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
        this.activeMedia = this.slides[0]?.dataset.mediaId;
        this.querySelectorAll('[data-gallery-controls], [data-quantity-minus], [data-quantity-plus]').forEach(node => { node.hidden = false; });
        this.dataset.ready = '';
        this.addEventListener('change', event => {
          if (event.target.matches('[data-variant-input]')) this.updateVariant(true);
          if (event.target === this.quantity) this.updateQuantity(0);
          if (event.target === this.plan) this.updatePrice();
        }, options);
        this.addEventListener('click', event => {
          const button = event.target.closest('button');
          if (!button) return;
          if (button.hasAttribute('data-quantity-minus')) this.updateQuantity(-1);
          if (button.hasAttribute('data-quantity-plus')) this.updateQuantity(1);
          if (button.dataset.mediaGo) this.showMedia(button.dataset.mediaGo);
        }, options);
        this.track?.addEventListener('scroll', () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = 0;
            const item = nearestItem(this.track, this.slides);
            if (item) this.markMedia(item.dataset.mediaId);
          });
        }, { ...options, passive: true });
        window.addEventListener('popstate', () => {
          const id = new URL(window.location.href).searchParams.get('variant');
          const input = this.querySelector('[data-variant-input]');
          const variant = Array.from(this.querySelectorAll('template[data-variant]')).find(item => item.dataset.variant === id);
          if (input && variant) {
            input.value = id;
            this.updateVariant(false);
          }
        }, options);
        this.updateVariant(false);
      }

      disconnectedCallback() {
        this.abort?.abort();
        cancelAnimationFrame(this.scrollFrame);
      }

      updateVariant(updateUrl) {
        const selected = this.querySelector('[data-variant-input]');
        if (!selected) return;
        this.variant = this.querySelector('template[data-variant="' + selected.value + '"]');
        if (!this.variant) return;
        const data = this.variant.dataset;
        this.addButton.disabled = data.available !== 'true';
        this.addButton.textContent = data.available === 'true' ? this.dataset.addLabel : this.dataset.soldLabel;
        this.quantity.min = data.min || '1';
        this.quantity.step = data.step || '1';
        if (data.max) this.quantity.max = data.max;
        else this.quantity.removeAttribute('max');
        if (this.plan) {
          const previous = this.plan.value;
          const template = this.querySelector('template[data-plans="' + selected.value + '"]');
          if (template) {
            this.plan.replaceChildren(template.content.cloneNode(true));
            if (Array.from(this.plan.options).some(option => option.value === previous)) this.plan.value = previous;
            if (this.plan.required && !this.plan.value) this.addButton.disabled = true;
          }
        }
        this.updatePrice();
        this.updateQuantity(0);
        if (data.media) this.showMedia(data.media);
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', selected.value);
          window.history.replaceState(window.history.state, '', url);
        }
      }

      updatePrice() {
        if (!this.variant) return;
        let price = this.variant;
        if (this.plan?.value) {
          price = this.querySelector('template[data-plan-price="' + this.variant.dataset.variant + '-' + this.plan.value + '"]') || price;
        }
        this.querySelector('[data-product-price]').replaceChildren(price.content.cloneNode(true));
        if (this.plan) this.addButton.disabled = this.variant.dataset.available !== 'true' || (this.plan.required && !this.plan.value);
      }

      updateQuantity(direction) {
        if (!this.quantity) return;
        const min = Number(this.quantity.min) || 1;
        const step = Number(this.quantity.step) || 1;
        const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
        const current = Number(this.quantity.value) || min;
        let value = min + Math.max(0, Math.round((current - min) / step) + direction) * step;
        const allowedMax = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
        value = Math.max(min, Math.min(allowedMax, value));
        this.quantity.value = String(value);
        this.querySelector('[data-quantity-minus]').disabled = value <= min;
        this.querySelector('[data-quantity-plus]').disabled = value + step > allowedMax;
        this.updateWhatsapp();
      }

      updateWhatsapp() {
        const link = this.querySelector('[data-whatsapp]');
        if (!link || !this.variant) return;
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', this.variant.dataset.variant);
        const message = [
          this.querySelector('h1').textContent.trim(),
          this.variant.dataset.title,
          '× ' + this.quantity.value,
          url.href
        ].join('\n');
        link.href = 'https://wa.me/' + encodeURIComponent(link.dataset.number) + '?text=' + encodeURIComponent(message);
      }

      showMedia(id) {
        const slide = this.slides.find(item => item.dataset.mediaId === id);
        if (slide) {
          scrollToItem(this.track, slide);
          this.markMedia(id);
        }
      }

      markMedia(id) {
        if (this.activeMedia !== id) {
          const previous = this.slides.find(item => item.dataset.mediaId === this.activeMedia);
          previous?.querySelectorAll('video').forEach(video => video.pause());
          previous?.querySelectorAll('iframe').forEach(frame => { frame.src = frame.src; });
          this.activeMedia = id;
        }
        this.querySelectorAll('[data-media-go]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.mediaGo === id));
        });
      }
    });
  }

  if (!customElements.get('noor-product-carousel')) {
    customElements.define('noor-product-carousel', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const options = { signal: this.abort.signal };
        this.track = this.querySelector('[data-carousel-track]');
        this.items = Array.from(this.querySelectorAll('[data-carousel-item]'));
        this.dots = Array.from(this.querySelectorAll('[data-carousel-go]'));
        if (!this.track || !this.items.length) return;
        const dots = this.querySelector('[data-carousel-dots]');
        if (dots) dots.hidden = false;
        this.addEventListener('click', event => {
          const button = event.target.closest('[data-carousel-go]');
          if (button) scrollToItem(this.track, this.items[Number(button.dataset.carouselGo)]);
        }, options);
        this.track.addEventListener('scroll', () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = 0;
            const item = nearestItem(this.track, this.items);
            const offset = Math.abs(this.track.scrollLeft);
            const maxOffset = this.track.scrollWidth - this.track.clientWidth;
            const index = offset <= 1 ? 0
              : offset >= maxOffset - 1 ? this.items.length - 1
              : this.items.indexOf(item);
            this.dots.forEach(dot => dot.setAttribute('aria-pressed', String(Number(dot.dataset.carouselGo) === index)));
          });
        }, { ...options, passive: true });
      }

      disconnectedCallback() {
        this.abort?.abort();
        cancelAnimationFrame(this.scrollFrame);
      }
    });
  }
})();
